import _ from 'underscore';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import TripForm from 'apps/my-trips/components/trip-form';
import TripShare from 'apps/my-trips/components/trip-share';
import LayoutSource from 'apps/layout/sources/layout-source';
import MyTripsUtils from 'apps/my-trips/utils/my-trips-utils';
import FeatureStore from 'apps/layout/stores/feature-store';
import TripLogStore from 'apps/my-trips/stores/trip-log-store';
import MeetingsStore from 'apps/my-trips/stores/meetings-store';
import MyTripsSource from 'apps/my-trips/sources/my-trips-source';
import TripLogSource from 'apps/my-trips/sources/trip-log-source';
import InteractionForm from 'apps/interactions/components/create/interaction-form';
import { isEmpty } from '@evertrue/sdk';
import { useIdentity } from 'base/identity-resolver';
import { useFluxStore } from '@evertrue/et-flux';
import { Modal, ActionsMenu } from '@evertrue/et-components';
import { useEffect, useState, cloneElement } from 'react';

const MeetingsStoreMap = (trip) => {
  const booked = MeetingsStore.getBookedContacts(trip?.id);
  const notBooked = MeetingsStore.getUnconfirmedContacts(trip?.id);
  return {
    booked,
    notBooked,
    hasMeetings: !isEmpty(MeetingsStore.getForTrip(trip?.id)),
    contactOptions: MyTripsUtils.getGroupedContactOptions(booked, notBooked),
  };
};

const TripLogStoreMap = (trip) => ({
  exportableLogs: TripLogStore.getExportableForTrip(trip?.id),
});

const FeatureStoreMap = () => ({
  hasInteractionExports: FeatureStore.hasInteractionExports(),
  hasInteractionWrites: FeatureStore.hasInteractionWrites(),
});

const TripActions = ({ trip = {}, openEventModal = () => {} }) => {
  const { exportableLogs } = useFluxStore(TripLogStore, () => TripLogStoreMap(trip));
  const { isSuperUser, isOwner } = useIdentity();
  const { hasMeetings, contactOptions } = useFluxStore(MeetingsStore, () => MeetingsStoreMap(trip));
  const { hasInteractionExports, hasInteractionWrites } = useFluxStore(FeatureStore, FeatureStoreMap);
  const [overlayKey, setOverlayKey] = useState();

  useEffect(() => {
    TripLogSource.fetchInteractions(trip.id);
  }, [trip.id]);

  const handleUnmountLegacyModal = () => {
    LayoutSource.removeOverlay(overlayKey);
    setOverlayKey(undefined);
  };

  // This is essentially replicating the behavior of the "ModalTrigger" component to handle situations where there are legacy modals (like the TripForm).
  // We'd like to eventually scrap all the legacy modals and only use the new ones, but that's outside the scope of this refactor -- just need an action menu that can support both.
  const openLegacyTripForm = () => {
    const dialog = cloneElement(<TripForm trip={trip} />, {
      remove: () => handleUnmountLegacyModal(),
      onUnmount: () => handleUnmountLegacyModal(),
    });

    const overlayKey = _.randomKey();
    setOverlayKey(overlayKey);

    LayoutSource.overlay(dialog, { key: overlayKey, top: 0, left: 0, bottom: 0, right: 0, zIndex: 900 });
  };

  const getOptions = () => {
    const options = [];

    if (trip.is_my_trip) {
      options.push({
        id: 'edit-trip',
        icon: 'edit',
        label: 'Edit Trip Details',
        onClick: openLegacyTripForm,
      });
      options.push({
        id: 'add-event',
        icon: 'add-meeting',
        label: 'Add Event',
        onClick: openEventModal,
      });
    }

    if (hasInteractionWrites) {
      options.push({
        id: 'add-interaction',
        label: 'Add Interaction',
        icon: 'trip-log',
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close}>
            <InteractionForm
              interaction={{ trip_id: trip.id }}
              contactOptions={contactOptions}
              referrer="trip"
              requestHide={close}
            />
          </Modal>
        ),
      });
    }

    if (hasMeetings && trip.is_my_trip) {
      options.push({
        id: 'email-trip-itinerary',
        label: 'Email Trip Itinerary',
        icon: 'email',
        mountOnClick: ({ is_open, close }) => (
          <Modal isOpen={is_open} closeModal={close}>
            <TripShare trip={trip} requestHide={close} />
          </Modal>
        ),
      });
    }

    if (hasInteractionExports && (isSuperUser || isOwner) && !isEmpty(exportableLogs)) {
      options.push({
        id: 'export-trip-log',
        label: 'Export Trip Log',
        icon: 'cloud-download',
        onClick: () => {
          EverTrue.Alert.confirm(
            {
              type: 'info',
              headline: 'Export Trip Log?',
              content: (
                <div>
                  <strong>{null}</strong> {trip?.name}. The exported .CSV file will include all interactions except
                  EverTrue Comments.
                </div>
              ),
              buttonLabel: 'Continue Export',
            },
            (e) => {
              if (e) {
                EverTrue.track.set('manual_export_action', { type: 'trip' });
                TripLogSource.startExport(trip?.id);
              }
            }
          );
        },
      });
    }

    if (trip.is_my_trip) {
      options.push({
        id: 'delete-trip',
        label: 'Delete Trip',
        icon: 'delete',
        className: 'text-danger',
        onClick: () => {
          EverTrue.Alert.confirm(
            {
              type: 'danger',
              headline: 'Delete Trip?',
              content: `Are you sure you want to delete your ${trip.name} trip? You will still have access to your constituent notes, but this action will permanently delete your meetings.`,
            },
            (e) => {
              if (e) {
                MyTripsSource.delete(trip?.id);
                EverTrue.Navigator('/trips', true);
              }
            }
          );
        },
      });
    }

    return options;
  };

  return <ActionsMenu options={getOptions()} />;
};

TripActions.propTypes = {
  trip: PropTypes.object.isRequired,
  openEventModal: PropTypes.func,
};

export default TripActions;
